import CallSplitIcon from "@mui/icons-material/CallSplit";
import DeveloperBoardIcon from "@mui/icons-material/DeveloperBoard";
import DeviceHubIcon from "@mui/icons-material/DeviceHub";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import MemoryIcon from "@mui/icons-material/Memory";
import ModeCommentOutlinedIcon from "@mui/icons-material/ModeCommentOutlined";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import PeopleIcon from "@mui/icons-material/People";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import type {SxProps, Theme} from "@mui/material/styles";
import type {SvgIconProps} from "@mui/material/SvgIcon";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Humanize from "humanize-plus";
import React from "react";

import {projectCardClasses as classes} from "./classes.js";

export type CounterType =
    | "Fork"
    | "Use"
    | "Comment"
    | "Project"
    | "Follower"
    | "Following"
    | "Layout"
    | "Component"
    | "Star"
    | "Pending"
    | "Thread"
    | "Footprint"
    | "Net"
    | "Price"
    | "Symbol";

export type CounterColor = "textPrimary" | "textSecondary";

export interface SimpleCounterProps {
    /**
     * The count value to display. If undefined or <= 0, the counter will not render.
     */
    count?: number;
    /**
     * The text color of the counter.
     * @default "textPrimary"
     */
    color?: CounterColor;
    /**
     * Optional click handler. When provided, the count text becomes a clickable button.
     */
    handleClick?: () => void;
    /**
     * Whether to render in compact mode (smaller font, only shows the count).
     * @default false
     */
    compact?: boolean;
    /**
     * Custom gap spacing between icon and text.
     * @default theme.spacing(0.5)
     */
    gap?: number;
    /** Custom CSS class for the icon element. */
    iconClass?: string;
    /** Emotion styles for the icon element. */
    iconSx?: SxProps<Theme>;
    /**
     * The type of counter, used for pluralization and semantic meaning.
     */
    counterType: CounterType;
    /**
     * Optional icon component to display before the count.
     */
    Icon?: React.ComponentType<SvgIconProps>;
    /**
     * Whether to pluralize the counter type based on count.
     * @default true
     */
    pluralize?: boolean;
    /**
     * Custom formatter for the count number.
     * @default Humanize.compactInteger(count, 1)
     * @example
     * formatCount={(count) => count.toLocaleString()} // "1,234" instead of "1.2K"
     */
    formatCount?: (count: number) => string;
    /**
     * Custom renderer for the entire content (overrides default formatting).
     * @param formattedCount - The formatted count string
     * @param pluralizedType - The pluralized counter type (e.g., "Comments", "Fork")
     * @example
     * renderContent={(count, type) => `${count} ${type}!`}
     */
    renderContent?: (formattedCount: string, pluralizedType?: string) => React.ReactNode;
    /**
     * Custom renderer for tooltip title. When provided, a tooltip will be shown on hover.
     * @param formattedCount - The formatted count string
     * @param pluralizedType - The pluralized counter type (e.g., "Comments", "Fork")
     * @example
     * renderTooltipTitle={(count, type) => `${count} ${type} in the layout`}
     */
    renderTooltipTitle?: (renderTooltipTitleParams: {
        count: number;
        formattedCount: string;
        pluralizedType?: string;
    }) => string;
}

/**
 * Props for counter variant components that have counterType and Icon preset.
 */
export type CounterVariantProps = Omit<SimpleCounterProps, "counterType" | "Icon">;

const defaultIconSx = {
    fontSize: "16px",
    width: "16px",
    height: "16px",
} as const;

const buttonSx = {
    border: "none",
    background: "none",
    padding: 0,
    font: "inherit",
    cursor: "pointer",
    textDecoration: "none",
    "&:hover": {
        textDecoration: "underline",
    },
} as const;

function SimpleCounterBase(props: SimpleCounterProps) {
    const {
        count,
        counterType,
        Icon,
        pluralize = true,
        handleClick,
        formatCount,
        renderContent,
        renderTooltipTitle,
        compact = false,
        color = "textPrimary",
        iconClass: iconClassProp,
        iconSx,
    } = props;
    if (count == null || count < 0) {
        return null;
    }

    const formattedCount = formatCount?.(count) ?? Humanize.compactInteger(count, 1);
    const pluralizedType = pluralize ? Humanize.pluralize(count, counterType) : counterType;

    const getContent = (): React.ReactNode => {
        if (renderContent) {
            return renderContent(formattedCount, pluralizedType);
        }
        return compact ? formattedCount : `${formattedCount} ${pluralizedType}`;
    };

    const content = getContent();
    const responsiveContent = compact ? (
        <span>{content}</span>
    ) : (
        <>
            <span className={classes.metricFullLabel}>{content}</span>
            <span className={classes.metricCompactLabel} aria-hidden>
                {formattedCount}
            </span>
        </>
    );
    const counterContent = (
        <Typography
            color={color}
            variant={compact ? "caption" : "body2"}
            sx={(theme: Theme) => ({
                display: "flex",
                alignItems: "center",
                gap: props.gap === undefined ? theme.spacing(0.5) : `${props.gap}px`,
                fontWeight: 700,
                [`& .${classes.metricCompactLabel}`]: {display: "none"},
            })}
            noWrap={compact}
        >
            {Icon && (
                <Icon className={iconClassProp} sx={[defaultIconSx, ...(Array.isArray(iconSx) ? iconSx : [iconSx])]} />
            )}

            {handleClick ? (
                <Typography
                    component="button"
                    variant={compact ? "caption" : "body2"}
                    color={color}
                    onClick={handleClick}
                    sx={buttonSx}
                >
                    {responsiveContent}
                </Typography>
            ) : (
                responsiveContent
            )}
        </Typography>
    );

    if (renderTooltipTitle) {
        const tooltipTitle = renderTooltipTitle({
            count,
            formattedCount,
            pluralizedType: pluralizedType.toLocaleLowerCase(),
        });
        return (
            <Tooltip title={tooltipTitle} placement="top">
                {counterContent}
            </Tooltip>
        );
    }

    return counterContent;
}

export const SimpleCounter = React.memo(SimpleCounterBase);

/**
 * Factory function to create a counter component variant.
 * @param counterType - The type of counter (used for display text and pluralization)
 * @param Icon - Optional icon component to display
 * @param defaultProps - Optional default props that can be overridden by the consumer
 */
export const createCounter = (
    counterType: CounterType,
    Icon?: React.ComponentType<SvgIconProps>,
    defaultProps?: Partial<CounterVariantProps>,
) => {
    const Counter = (props: CounterVariantProps) => (
        <SimpleCounter counterType={counterType} Icon={Icon} {...defaultProps} {...props} />
    );
    Counter.displayName = `${counterType}Counter`;
    return Counter;
};

export const CommentCounter = createCounter("Comment", ModeCommentOutlinedIcon);
export const ForkCounter = createCounter("Fork", CallSplitIcon);
export const UsageCounter = createCounter("Use", ShowChartIcon);
export const ProjectCounter = createCounter("Project", FolderOpenIcon);
export const ComponentCounter = createCounter("Component", MemoryIcon);
export const FollowerCounter = createCounter("Follower", PeopleIcon);
export const FollowingCounter = createCounter("Following", undefined, {pluralize: false});
export const LayoutCounter = createCounter("Layout", DeveloperBoardIcon);
export const PriceCounter = createCounter("Price", MonetizationOnIcon, {
    formatCount: (count) => count.toFixed(2),
    renderContent: (formattedCount) => `~$${formattedCount}`,
});
export const StarCounter = createCounter("Star", StarBorderIcon);
export const SymbolCounter = createCounter("Symbol", DeviceHubIcon);
